import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed } from 'vue';
import { FormKit } from '@formkit/vue';
import { IDropdownItem } from '@/interfaces';
import { useGoogleMapStore } from '@/store';


export default /*@__PURE__*/_defineComponent({
  __name: 'GoogleMapAddressInput',
  props: {
    label: {},
    outerClass: {},
    innerClass: {},
    wrapperClass: {},
    placeholder: {},
    requiredValidationMessage: {}
  },
  emits: ['selected'],
  setup(__props: any, { emit: __emit }) {

// Add this line to reference the Google Maps types
/// <reference types="@types/google.maps" />

const props = __props;

const googleMapStore = useGoogleMapStore();
const formattedAddress = computed(() => googleMapStore.formattedAddress);
const requiredValidationMessage = computed(() => props.requiredValidationMessage ?? 'Please provide a valid address');

const emit = __emit;

async function getSuggestions({ search }: { search: string }): Promise<IDropdownItem[]> {
  try {
    if (!search || search.length < 3) return [];

    // Create a session token.
    const token = new google.maps.places.AutocompleteSessionToken();
    // Add an initial request body.
    let request: google.maps.places.AutocompleteRequest = {
      input: search,
      origin: { lat: -27.5866031, lng: 153.1018371 },
      includedRegionCodes: ['au'], // only allow au address
      language: 'en-AU',
      sessionToken: token,
    };

    if (googleMapStore.suburbPostcodeMode) request.includedPrimaryTypes = ['locality', 'postal_code'];

    const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
    return suggestions
      .sort((a) => {
        return a.placePrediction?.secondaryText?.text?.includes('QLD') ? -1 : 1;
      })
      .map((suggestion: google.maps.places.AutocompleteSuggestion) => ({
        label: suggestion.placePrediction?.text.toString() ?? '',
        value: {
          prediction: suggestion.placePrediction,
          label: suggestion.placePrediction?.text.toString() ?? '',
        },
      }));
  } catch (error) {
    console.warn('Unable to lookup: ', error);
    return [];
  }
}

const selectSuggestion = async (suggestion: { prediction: google.maps.places.PlacePrediction; label: string }) => {
  if (!suggestion?.prediction || !(suggestion?.prediction instanceof google.maps.places.PlacePrediction)) return;
  try {
    const place = suggestion.prediction.toPlace();
    await place.fetchFields({ fields: ['location', 'formattedAddress', 'id', 'addressComponents'] });

    const formattedAddress = googleMapStore.suburbPostcodeMode ? suggestion.label : place.formattedAddress;
    googleMapStore.setFormattedAddress(formattedAddress ?? '');
    googleMapStore.setLatitude(place.location?.lat() ?? 0);
    googleMapStore.setLongitude(place.location?.lng() ?? 0);
    googleMapStore.setAddressStateFromAddressComponents(place.addressComponents);

    document.getElementsByName('address')?.[0]?.blur();
    emit('selected', { lat: place.location?.lat() ?? 0, lng: place.location?.lng() ?? 0 });
  } catch (error) {
    console.warn('Unable to fetch place details: ', error);
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(FormKit), {
    modelValue: formattedAddress.value,
    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formattedAddress).value = $event)),
    type: "autocomplete",
    name: "address",
    "aria-label": "Breakdown Location",
    label: _ctx.label ?? '',
    placeholder: _ctx.placeholder ?? 'Enter location',
    options: getSuggestions,
    prefixIcon: "search",
    wrapperClass: _ctx.wrapperClass ?? 'flex block',
    outerClass: _ctx.outerClass ?? '$remove:pb-4',
    listboxButtonClass: "$reset hidden",
    selectIconClass: "$reset hidden",
    innerClass: _ctx.innerClass ?? '$remove:md:max-w-[300px] $remove:md:px-4 gap-2 md:px-3',
    inputClass: "$remove:px-3 font-sans !font-normal !leading-none text-base pr-3 pl-0 placeholder-racq-grey",
    prefixIconClass: "$remove:bg-gradient-to-b $remove:border-gray-400 $remove:border-r $remove:[&>svg]:max-w-[1em] $remove:[&>svg]:max-h-[1em] flex-wrap content-center w-6 [&>svg]:max-w-6 [&>svg]:max-h-6 [&>svg]:w-6 [&>svg]:h-6 text-racq-light-grey",
    listboxClass: "font-sans !font-normal !leading-none text-base",
    messagesClass: "$reset list-none flex ml-0 pl-0",
    validation: "required",
    validationMessages: {
      required: requiredValidationMessage.value,
    },
    validationVisibility: "submit",
    onInput: selectSuggestion
  }, null, 8, ["modelValue", "label", "placeholder", "wrapperClass", "outerClass", "innerClass", "validationMessages"]))
}
}

})